import { EFinancingCompany, getEFinancingCompanyList } from '@if/core/types'
import { useIfUser } from '@if/mui'
import { SELECTED_COMPANY_STORAGE_KEY } from '@src/types/constants'
import { useSyncExternalStore } from 'react'
import { useLocation, useMatch, useMatches, useParams } from 'react-router-dom'
import { toCompanySwitchPath } from './company-path'

/**
 * The company lives in the URL (`/:company/...`) on every screen that depends on it. What follows only covers
 * what the URL cannot: the company a company-less URL opens on, and what the selector and the nav show on a
 * screen without one. That is the company the user worked in last, kept in localStorage so a new tab, or a
 * bookmark of a company-less URL, lands where the user left off.
 */

const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  // Another tab picking a company changes what this one opens on next.
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

const readLastCompany = (): string | null => localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY)

export const saveLastCompany = (company: EFinancingCompany) => {
  if (readLastCompany() === company) return
  localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, company)
  listeners.forEach((listener) => listener())
}

/** The company the user worked in last, or their first one. */
export const useLastCompany = (): EFinancingCompany => {
  const { companyIds } = useIfUser()
  const lastCompany = useSyncExternalStore(subscribe, readLastCompany) as EFinancingCompany | null
  return lastCompany && companyIds.includes(lastCompany) ? lastCompany : companyIds[0]
}

const isFinancingCompany = (value: string | undefined): value is EFinancingCompany =>
  getEFinancingCompanyList().includes(value as EFinancingCompany)

/**
 * The URL's company, or null on a screen without one. Matched on the location rather than read from the route,
 * so it also works from the layout, above the routes.
 */
export const useUrlCompany = (): EFinancingCompany | null => {
  const company = useMatch('/:company/*')?.params.company
  return isFinancingCompany(company) ? company : null
}

/** The company the selector and the nav show: the URL's, or the last one on a screen without one. */
export const useCurrentCompany = (): EFinancingCompany => {
  const urlCompany = useUrlCompany()
  const lastCompany = useLastCompany()
  return urlCompany ?? lastCompany
}

/** The company of a screen under `/:company`, which `CompanyRoute` has already checked. */
export const useCompany = (): EFinancingCompany => {
  const { company } = useParams()
  return company as EFinancingCompany
}

export const useCompanySwitchPath = (): ((company: EFinancingCompany) => string) => {
  const matches = useMatches()
  const { pathname } = useLocation()
  // The deepest match carries the params of every level above it.
  const params = matches.at(-1)?.params ?? {}
  return (company) => toCompanySwitchPath(pathname, params, company)
}
